import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const propertyTypes = [
  { value: "villa", label: "Villa" },
  { value: "radhus", label: "Radhus" },
  { value: "brf", label: "BRF / Flerfamiljshus" },
  { value: "fastighet", label: "Fastighet / Kommersiellt" },
  { value: "annat", label: "Annat" },
];

export default function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    property_type: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, property_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.phone || !formData.email || !formData.property_type) {
      toast.error("Vänligen fyll i alla obligatoriska fält");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/leads`, formData);
      toast.success("Tack för din förfrågan! Vi återkommer inom 24 timmar.");
      setFormData({
        name: "",
        phone: "",
        email: "",
        property_type: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Något gick fel. Vänligen försök igen eller ring oss.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact-form" className="section-padding bg-slate-50" data-testid="contact-form-section">
      <div className="container mx-auto max-w-6xl">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left - Info */}
          <div className="space-y-8">
            <div className="space-y-4">
              <span className="text-sm font-semibold tracking-wider uppercase text-[#b91c1c]">
                Kontakta oss
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 tracking-tight">
                Få en kostnadsfri offert
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Fyll i formuläret så kontaktar vi dig inom 24 timmar med en personlig offert. 
                Helt kostnadsfritt och utan köptvång.
              </p>
            </div>

            {/* Contact Info */}
            <div className="space-y-4 pt-4">
              <div className="flex items-center gap-4 text-slate-700">
                <div className="w-12 h-12 bg-[#b91c1c]/10 rounded-xl flex items-center justify-center">
                  <Phone className="w-5 h-5 text-[#b91c1c]" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Ring oss</p>
                  <p className="font-semibold">08-25 46 04</p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-slate-700">
                <div className="w-12 h-12 bg-[#b91c1c]/10 rounded-xl flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#b91c1c]" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">E-post</p>
                  <p className="font-semibold">info@vimalar.se</p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-slate-700">
                <div className="w-12 h-12 bg-[#b91c1c]/10 rounded-xl flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#b91c1c]" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Verksamma i</p>
                  <p className="font-semibold">Stockholm med omnejd</p>
                </div>
              </div>
            </div>

            {/* Trust note */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200">
              <p className="text-sm text-slate-600">
                <strong className="text-slate-900">Integritet:</strong> Vi delar aldrig dina uppgifter med tredje part. 
                Din information används endast för att kontakta dig angående din offertförfrågan.
              </p>
            </div>
          </div>

          {/* Right - Form */}
          <div className="bg-white p-8 md:p-10 rounded-3xl shadow-xl border border-slate-100">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="lead-form">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-700 font-medium">
                  Namn *
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Ditt namn"
                  value={formData.name}
                  onChange={handleChange}
                  className="h-12 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-[#b91c1c]/20 focus:border-[#b91c1c]"
                  data-testid="input-name"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-slate-700 font-medium">
                  Telefon *
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="07X XXX XX XX"
                  value={formData.phone}
                  onChange={handleChange}
                  className="h-12 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-[#b91c1c]/20 focus:border-[#b91c1c]"
                  data-testid="input-phone"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700 font-medium">
                  E-post *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="din@email.se"
                  value={formData.email}
                  onChange={handleChange}
                  className="h-12 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-[#b91c1c]/20 focus:border-[#b91c1c]"
                  data-testid="input-email"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="property_type" className="text-slate-700 font-medium">
                  Typ av fastighet *
                </Label>
                <Select value={formData.property_type} onValueChange={handleSelectChange}>
                  <SelectTrigger 
                    className="h-12 bg-slate-50 border-slate-200 focus:ring-2 focus:ring-[#b91c1c]/20"
                    data-testid="select-property-type"
                  >
                    <SelectValue placeholder="Välj typ av fastighet" />
                  </SelectTrigger>
                  <SelectContent>
                    {propertyTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-slate-700 font-medium">
                  Meddelande (valfritt)
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Beskriv gärna ditt projekt eller ställ frågor..."
                  value={formData.message}
                  onChange={handleChange}
                  className="min-h-[120px] bg-slate-50 border-slate-200 focus:ring-2 focus:ring-[#b91c1c]/20 focus:border-[#b91c1c] resize-none"
                  data-testid="input-message"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#b91c1c] hover:bg-[#991b1b] text-white h-14 rounded-xl text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                data-testid="submit-button"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Skickar...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Skicka förfrågan
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-slate-500">
                Genom att skicka godkänner du vår integritetspolicy
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
